import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { green, red } from '@material-ui/core/colors';
import {
  ApiFilterOperation,
  DetailContext,
  DetailToolbarButton2,
  DetailToolbarButtonProps,
  EmptyComponent,
  Evidence,
  UserContext,
} from '@eas/common-web';
import { useAuthoredEvidence } from '@composite/evidence/authored-evidence';
import { Me, Task, UserRole } from '@models';
import {
  EntityName,
  EsFileState,
  EvidenceApiUrl,
  Role,
  TaskState,
} from '@enums';
import { ActionBar } from './task-actionbar';
import { useColumns } from './task-columns';
import { Fields } from './task-fields';
import { useValidationSchema } from './task-schema';

export const createRowStyle = (deadline: Task) => {
  switch (deadline.state) {
    case TaskState.PROCESSED:
      return {
        color: green[500],
        backgroundColor: green[50],
      };
    case TaskState.CANCELED:
      return {
        color: red[500],
        backgroundColor: red[50],
      };
    default:
      return {};
  }
};

const CustomEditButton = (props: DetailToolbarButtonProps) => {
  const { user } = useContext(UserContext);
  const { source } = useContext(DetailContext);
  const item = source?.data as Task;

  const activeSubstitutionUserId =
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id;

  const currentUserId = activeSubstitutionUserId || user?.id;

  const isSuperadmin = user?.userRoles?.some(
    (r: UserRole) => r.role === Role.SUPERADMIN
  );
  const isCreator = item?.createdBy?.id === currentUserId;
  const isCreatorByDelegate = item?.createdByDelegate?.id === currentUserId;
  const isSolver = item?.solver?.id === currentUserId;

  const canEdit = isSuperadmin || isCreator || isSolver || isCreatorByDelegate;

  if (!canEdit) {
    return <DetailToolbarButton2 {...props} disabled={true} />;
  }

  return <DetailToolbarButton2 {...props} />;
};

const getDefaultPreFilters = (
  user: Me,
  hasPermission: UserContext<Me>['hasPermission']
) => {
  const activeSubstitutionUserId =
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id;

  const getFilters = (userId: string) => [
    {
      operation: ApiFilterOperation.OR,
      filters: [
        {
          field: 'solver.id',
          value: userId,
          operation: ApiFilterOperation.EQ,
        },
        {
          field: 'createdBy.id',
          value: userId,
          operation: ApiFilterOperation.EQ,
        },
        {
          field: 'createdByDelegate.id',
          value: userId,
          operation: ApiFilterOperation.EQ,
        },
      ],
    },
  ];

  if (activeSubstitutionUserId) {
    return getFilters(activeSubstitutionUserId);
  }

  if (hasPermission(Role.ADMIN)) {
    return [];
  }

  return getFilters(user.id);
};

export const TASK_IDENTIFIER = 'TASKS';
export const TASK_VERSION = 16;

export function Tasks() {
  const intl = useIntl();
  const validationSchema = useValidationSchema();

  const { user, hasPermission } = useContext(UserContext);

  const activeSubstitutionUserId =
    user?.additionalDetails?.activeSubstitution?.substitutedUser?.id;

  const defaultPreFilters = getDefaultPreFilters(user, hasPermission);

  const evidence = useAuthoredEvidence<Task>({
    version: TASK_VERSION,
    identifier: TASK_IDENTIFIER,
    apiProps: {
      url: EvidenceApiUrl.TASKS,
    },
    tableProps: {
      columns: useColumns(),
      tableName: intl.formatMessage({
        id: 'ES__TASKS__TABLE__TITLE',
        defaultMessage: 'Úlohy',
      }),
      defaultSorts: [
        {
          field: 'created',
          datakey: 'created',
          order: 'DESC',
          type: 'FIELD',
        },
      ],
      createRowStyle: createRowStyle,
      showReportButton: false,
      defaultPreFilters,
    },
    detailProps: {
      entityName: EntityName.TASK,
      validationSchema,
      FieldsComponent: Fields,
      GeneralFieldsComponent: EmptyComponent,
      lock: (item) =>
        item?.esFile?.state === EsFileState.REGISTRY ||
        item?.state == TaskState.PROCESSED ||
        item?.state == TaskState.CANCELED,
      toolbarProps: {
        title: intl.formatMessage({
          id: 'ES__TASKS__DETAIL__TITLE',
          defaultMessage: 'Úloha',
        }),
        showBtn: (btn) => !['NEW', 'REMOVE'].includes(btn),
        subtitle: (task) => task?.name ?? '',
        ActionBar,
        EditButton: CustomEditButton,
      },
    },
  });

  return <Evidence key={`${activeSubstitutionUserId}`} {...evidence} />;
}
